/**
 * Customer org team API (requires JWT from signup/login).
 * Session handling (refresh, 401 retry) lives in ../auth/customerSession.ts.
 */

import { customerAuthorizedFetch } from '../auth/customerSession';

function parseDetail(data: unknown): string {
  if (data && typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.detail === 'string') return d.detail;
    if (Array.isArray(d.detail) && d.detail[0] && typeof d.detail[0] === 'object') {
      const first = d.detail[0] as Record<string, unknown>;
      if (typeof first.msg === 'string') return first.msg;
    }
    if (d.detail && typeof d.detail === 'object') {
      const inner = d.detail as Record<string, unknown>;
      if (typeof inner.message === 'string') return inner.message;
    }
    if (typeof d.message === 'string') return d.message;
  }
  return 'Request failed';
}

export type BackendTeamUserRow = Record<string, unknown> & {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  mobile?: string;
  first_name?: string | null;
  last_name?: string | null;
  employee_id?: string | null;
  department?: string | null;
  job_title?: string | null;
  account_status?: string | null;
  blocked_until?: string | null;
  access_until?: string | null;
  disabled_features?: unknown;
  created_at?: string;
  updated_at?: string;
};

export async function listTeamUsers(): Promise<BackendTeamUserRow[]> {
  const res = await customerAuthorizedFetch('/api/customer/team/users', {
    method: 'GET',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
  const users = (data as { users?: BackendTeamUserRow[] }).users;
  return Array.isArray(users) ? users : [];
}

export type CreateTeamUserPayload = {
  email: string;
  name: string;
  role_id?: string;
  phone_number?: string;
  employee_id?: string;
  department?: string;
  job_title?: string;
  first_name?: string;
  last_name?: string;
  send_invitation: boolean;
  password?: string;
  auto_generate_password: boolean;
  access_type: 'permanent' | 'temporary' | 'until_blocked';
  access_start_date?: string;
  access_end_date?: string;
};

export async function createTeamUser(body: CreateTeamUserPayload): Promise<Record<string, unknown>> {
  const res = await customerAuthorizedFetch('/api/customer/team/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
  return data as Record<string, unknown>;
}

export type PatchTeamUserPayload = {
  name?: string;
  email?: string;
  role?: string;
  phone_number?: string;
  employee_id?: string;
  department?: string;
  job_title?: string;
  account_status?: string;
  blocked_until?: string | null;
  access_until?: string | null;
  disabled_features?: string[];
};

export async function patchTeamUser(userId: string, body: PatchTeamUserPayload): Promise<void> {
  const res = await customerAuthorizedFetch(`/api/customer/team/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
}

export async function deleteTeamUser(userId: string): Promise<void> {
  const res = await customerAuthorizedFetch(`/api/customer/team/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
}

export async function setTeamUserPassword(userId: string, newPassword: string): Promise<void> {
  const res = await customerAuthorizedFetch(`/api/customer/team/users/${encodeURIComponent(userId)}/password`, {
    method: 'POST',
    body: JSON.stringify({ new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
}

export async function resendTeamInvite(userId: string): Promise<{ invitation_sent?: boolean }> {
  const res = await customerAuthorizedFetch(`/api/customer/team/users/${encodeURIComponent(userId)}/resend-invite`, {
    method: 'POST',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseDetail(data));
  return data as { invitation_sent?: boolean };
}
